/**
 * `useState`, but the value lives in the query string.
 *
 * Every list page kept its filters, search text and page number in component
 * state only. Three consequences an admin hits constantly: F5 silently resets a
 * filtered triage list to unfiltered page 1; browser Back after opening one row
 * does the same, so you lose your place in a 79-row result; and no filtered view
 * can be pasted to a colleague. Keeping the state in the URL fixes all three at
 * once, because the URL *is* the state.
 *
 * Defaults are omitted from the URL so a clean list stays a clean link.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

type Serializer<T> = {
  parse: (raw: string | null) => T;
  format: (value: T) => string | null;
};

/** Same call shape as `useState`'s setter, including the updater form. */
type SetUrlState<T> = (next: T | ((prev: T) => T)) => void;

export function use_url_state<T>(key: string, serializer: Serializer<T>): [T, SetUrlState<T>] {
  const [params, set_params] = useSearchParams();
  const raw = params.get(key);
  const value = useMemo(() => serializer.parse(raw), [raw, serializer]);

  const set_value = useCallback<SetUrlState<T>>(
    (next) => {
      // Base every write on `window.location.search`, not on the params object
      // React Router hands back.
      //
      // Two setters in one handler is the normal case here — changing a filter
      // also resets the page — and the updater form of `setSearchParams` gives
      // both calls the *same* pre-update snapshot within a batch, so the second
      // silently discarded the first. (Observed: picking a status reset the page
      // correctly and dropped the status.) `navigate` writes to history
      // synchronously, so the live URL already carries the previous call's
      // result by the time the next one runs.
      const updated = new URLSearchParams(window.location.search);
      const prev = serializer.parse(updated.get(key));
      const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
      const formatted = serializer.format(resolved);
      if (formatted === null) updated.delete(key);
      else updated.set(key, formatted);
      set_params(updated, { replace: true });
    },
    [key, serializer, set_params],
  );

  return [value, set_value];
}

/** A string filter. Empty string is the default and stays out of the URL. */
export const string_param: Serializer<string> = {
  parse: (raw) => raw ?? '',
  format: (value) => (value.trim() === '' ? null : value),
};

/** A 1-based page number. Page 1 is the default and stays out of the URL. */
export const page_param: Serializer<number> = {
  parse: (raw) => {
    const n = Number(raw);
    return Number.isInteger(n) && n >= 1 ? n : 1;
  },
  format: (value) => (value <= 1 ? null : String(value)),
};

/**
 * An enum-ish filter restricted to a known set — anything else in the URL is
 * treated as absent, so a hand-edited or stale link degrades to "no filter"
 * instead of sending garbage to the API.
 */
export function enum_param<T extends string>(allowed: readonly T[]): Serializer<T | ''> {
  return {
    parse: (raw) => (raw && (allowed as readonly string[]).includes(raw) ? (raw as T) : ''),
    format: (value) => (value === '' ? null : value),
  };
}

/**
 * A page size, restricted to the sizes the toolbar offers. Anything else in the
 * URL falls back to the default rather than being forwarded to the API, which
 * caps `page_size` at 100 and 422s above it.
 */
export const PAGE_SIZES = [20, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 20;

export const page_size_param: Serializer<number> = {
  parse: (raw) => {
    const n = Number(raw);
    return (PAGE_SIZES as readonly number[]).includes(n) ? n : DEFAULT_PAGE_SIZE;
  },
  format: (value) => (value === DEFAULT_PAGE_SIZE ? null : String(value)),
};

export type SortState = { by: string; dir: 'asc' | 'desc' } | null;

/**
 * Column sort, carried as one `sort=created_at:desc` param.
 *
 * One param rather than `sort_by` + `sort_dir` because the two can never be
 * meaningfully set apart — a direction with no column is nonsense — and a
 * single token is what gets pasted into Slack when someone shares "the oldest
 * unpaid orders" view.
 *
 * `allowed` is the whitelist of sortable columns. A stale or hand-edited link
 * naming a column that no longer exists degrades to the default sort instead
 * of sending the API a field it will reject.
 */
export function sort_param(allowed: readonly string[], fallback: SortState = null): Serializer<SortState> {
  return {
    parse: (raw) => {
      if (!raw) return fallback;
      const [by, dir] = raw.split(':');
      if (!by || !allowed.includes(by)) return fallback;
      return { by, dir: dir === 'asc' ? 'asc' : 'desc' };
    },
    format: (value) => {
      if (!value) return null;
      // Keep the default sort out of the URL so an untouched list stays a
      // clean link.
      if (fallback && value.by === fallback.by && value.dir === fallback.dir) return null;
      return `${value.by}:${value.dir}`;
    },
  };
}

/**
 * A multi-select facet, carried as a comma-separated list. Values outside
 * `allowed` are dropped rather than passed through.
 */
export function multi_enum_param<T extends string>(allowed: readonly T[]): Serializer<T[]> {
  return {
    parse: (raw) => {
      if (!raw) return [];
      const seen = new Set<string>();
      return raw
        .split(',')
        .map((v) => v.trim())
        .filter((v) => (allowed as readonly string[]).includes(v) && !seen.has(v) && seen.add(v))
        .map((v) => v as T);
    },
    // Sorted so the same selection always produces the same URL — otherwise
    // picking A then B and B then A are two different cache keys for one query.
    format: (value) => (value.length === 0 ? null : [...value].sort().join(',')),
  };
}

/** A tri-state boolean filter: unset / true / false. */
export const bool_param: Serializer<'' | 'true' | 'false'> = {
  parse: (raw) => (raw === 'true' || raw === 'false' ? raw : ''),
  format: (value) => (value === '' ? null : value),
};

/** A positive number filter (amount floors/ceilings). Empty string = unset. */
export const number_param: Serializer<string> = {
  parse: (raw) => {
    if (!raw) return '';
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? raw : '';
  },
  format: (value) => (value.trim() === '' ? null : value),
};
